import type { OrderUnifiedOutput } from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { usePersistedServerTableState } from "@/hooks/usePersistedServerTableState";
import { useTranslation } from "@/hooks/useTranslation";
import {
	STATUS_COLOR_TO_CHIP_VARIANT,
	SectionCard,
} from "@/pages/dashboard/components";
import { useGetOrders } from "@/services";
import { getTimeSinceText } from "@/utils/timestamp";
import {
	calculateTaxPercentage,
	formatNumber,
	getOrderEmail,
} from "@/utils/tools";
import { useOrderStatusTranslator } from "@/utils/translations";
import { Chip, DataTable, Typography } from "@st-marketplace/ui";
import { Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type FC, useMemo } from "react";

/** Every user's orders, newest first. */
export const AdminOrdersCard: FC = () => {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();
	const translateStatus = useOrderStatusTranslator(t);
	const server = usePersistedServerTableState("st-admin-dashboard-orders", {
		initialPageSize: 10,
	});

	const { data, isLoading, isFetching, error, refetch } = useGetOrders({
		query: { page: server.pageIndex + 1, page_size: server.pageSize },
	});
	const rows = (data?.results ?? []) as OrderUnifiedOutput[];

	const columns = useMemo<ColumnDef<OrderUnifiedOutput>[]>(
		() => [
			{
				id: "email",
				header: t("admin.dashboard.orderEmail"),
				cell: ({ row }) => (
					<Link
						to="/orders/$orderId"
						params={{ orderId: row.original.pk }}
						className="break-all text-primary hover:underline"
					>
						{getOrderEmail(row.original)}
					</Link>
				),
			},
			{
				id: "buyer",
				header: t("order.tableBuyer"),
				cell: ({ row }) => row.original.buyer_name,
			},
			{
				id: "seller",
				header: t("order.tableSeller"),
				cell: ({ row }) => row.original.seller_name,
			},
			{
				id: "amount",
				header: t("order.tableDeliveryAmount"),
				cell: ({ row }) =>
					`${formatNumber(
						row.original.delivered / 1000,
						"count",
					)} / ${formatNumber(row.original.amount / 1000, "count")}${t(
						"signs.thousands",
					)}`,
			},
			{
				id: "unit_price",
				header: t("admin.dashboard.unitPrice"),
				cell: ({ row }) => formatNumber(row.original.unit_price, "price"),
			},
			{
				id: "tax",
				header: t("admin.dashboard.tax"),
				cell: ({ row }) => {
					const { delivered } = row.original;
					const taken = "taken" in row.original ? row.original.taken : 0;
					return delivered
						? formatNumber(calculateTaxPercentage(delivered, taken), "percent")
						: "-";
				},
			},
			{
				id: "status",
				header: t("order.tableStatus"),
				cell: ({ row }) => {
					const { text, color } = translateStatus(row.original.status);
					return (
						<Chip
							variant={STATUS_COLOR_TO_CHIP_VARIANT[color]}
							size="sm"
							className="whitespace-nowrap"
						>
							{text}
						</Chip>
					);
				},
			},
			{
				id: "date",
				header: t("order.tableDate"),
				cell: ({ row }) =>
					(row.original.created_date &&
						getTimeSinceText(row.original.created_date, t, lang)) ||
					"-",
			},
		],
		[t, lang, translateStatus],
	);

	return (
		<SectionCard
			title={t("admin.dashboard.orders")}
			viewAllTo="/orders"
			metric={
				<Typography variant="caption" as="p" className="text-secondary-icon">
					{t("admin.dashboard.ordersCount", { count: data?.count ?? 0 })}
				</Typography>
			}
		>
			<DataTable
				columns={columns.map((c) => ({ ...c, enableSorting: false }))}
				data={rows}
				totalItems={data?.count ?? 0}
				server={server}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => row.pk}
			/>
		</SectionCard>
	);
};
